import { createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from '@store/redux.store'
import { taskTrackerApi } from '../../api/taskTracker.api'
import { isTaskTrackerStickerFieldFilterable } from '../../lib/taskTrackerBoard.stickerFilters.utils'
import {
  mapAllBoardStickersToBoardStickerRows,
  mapBoardStickersToBoardStickerRows,
} from '../../lib/taskTrackerBoardStickerRows.utils'
import { selectTaskTrackerBoardFilterStickers } from './boardFilterStickers.selectors'
import type { TaskTrackerState } from '../index'
import { getErrorMessage } from '../shared/thunk.utils'

type AppState = RootState & {
  taskTracker: TaskTrackerState
}

export type FetchBoardFilterStickersPayload = {
  boardId: number
  silent?: boolean
}

const pendingBoardFilterStickersRequests = new Set<number>()

const mapBoardStickersToFilterRows = (
  stickers: Awaited<
    ReturnType<typeof taskTrackerApi.getBoardStickers>
  >['stickers'],
) =>
  stickers
    .filter(
      (sticker) =>
        sticker.isVisible &&
        isTaskTrackerStickerFieldFilterable(sticker.fieldType),
    )
    .sort((a, b) => a.position - b.position || a.id - b.id)
    .map((sticker) => ({
      fieldSettingId: sticker.fieldSettingId,
      fieldType: sticker.fieldType,
      title: sticker.title,
      color: sticker.color,
      options: sticker.options,
      systemKey: sticker.systemKey ?? null,
    }))

export const fetchBoardFilterStickersThunk = createAsyncThunk(
  'taskTracker/fetchBoardFilterStickers',
  async (payload: FetchBoardFilterStickersPayload, { rejectWithValue }) => {
    try {
      const boardData = await taskTrackerApi.getBoardStickers(payload.boardId)
      return {
        boardId: payload.boardId,
        silent: payload.silent ?? false,
        rows: mapBoardStickersToFilterRows(boardData.stickers),
        visibleStickerRows: mapBoardStickersToBoardStickerRows(
          boardData.stickers,
        ),
        allStickerRows: mapAllBoardStickersToBoardStickerRows(
          boardData.stickers,
        ),
      }
    } catch (error: unknown) {
      return rejectWithValue({
        boardId: payload.boardId,
        silent: payload.silent ?? false,
        error: getErrorMessage(error, 'Не удалось загрузить стикеры доски'),
      })
    } finally {
      pendingBoardFilterStickersRequests.delete(payload.boardId)
    }
  },
  {
    condition: (payload, { getState }) => {
      if (pendingBoardFilterStickersRequests.has(payload.boardId)) {
        return false
      }

      if (payload.silent) {
        pendingBoardFilterStickersRequests.add(payload.boardId)
        return true
      }

      const boardState = selectTaskTrackerBoardFilterStickers(
        getState() as AppState,
        payload.boardId,
      )

      if (boardState.loading || boardState.isInitialized) {
        return false
      }

      pendingBoardFilterStickersRequests.add(payload.boardId)
      return true
    },
  },
)
